import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Clock, Info } from 'lucide-react'
import { useBot, useUpdateBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'

export default function BotTimeout() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: bot, isLoading } = useBot(botId)
  const updateBot = useUpdateBot()

  const [timeoutHours, setTimeoutHours] = useState<number>(1)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)

  // Store handleSave ref to avoid recreating MainButton
  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize timeout from bot data only once
  useEffect(() => {
    if (bot?.timeout !== undefined && !isInitialized) {
      // Convert seconds to hours
      setTimeoutHours(bot.timeout / 3600)
      setIsInitialized(true)
    }
  }, [bot, isInitialized])

  // Reset success state after 2 seconds
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess])

  const handleSave = async () => {
    if (!bot) return

    // Validate timeout (0.5 to 24 hours)
    if (timeoutHours < 0.5 || timeoutHours > 24) {
      setError('Введите значение от 0.5 до 24 часов')
      hapticNotification('error')
      return
    }

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      // Convert hours to seconds
      const timeoutSeconds = Math.round(timeoutHours * 3600)

      await updateBot.mutateAsync({
        botId: bot.id,
        data: {
          timeout: timeoutSeconds,
        },
      })
      hapticNotification('success')
      setShowSuccess(true)

      // Reset loading state and hide main button before navigation
      setMainButtonLoading(false)
      hideMainButton()
      document.body.style.backgroundColor = ''

      // Scroll to top before navigation
      window.scrollTo(0, 0)

      // Navigate back to bot details page with success message
      navigate(`/bots/${botId}`, {
        replace: true,
        state: { successMessage: 'Бот успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update timeout:', error)
      setError('Ошибка при сохранении таймаута')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  // Update ref with latest handleSave
  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  // Setup MainButton and page background
  useEffect(() => {
    if (!isReady) return

    // Scroll to top on mount
    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    // Reset loading state and show button
    setMainButtonLoading(false)
    showMainButton('Сохранить таймаут', onSave, '#5288c1', '#ffffff')

    // Set page background color like bottom nav
    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  const presetValues = [1, 3, 6, 12, 24]

  if (isLoading || !bot) {
    return <div className="min-h-screen bg-tg-bg flex items-center justify-center">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#5288c1]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Таймаут</h1>
              <p className="text-sm text-tg-hint mt-0.5">@{bot.username}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Timeout Settings Card */}
        <div
          className="rounded-xl p-4 space-y-3 border-2 transition-all"
          style={{
            borderColor: '#5288c133',
            backgroundColor: '#5288c10d'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#5288c11a' }}
              >
                <Clock className="w-5 h-5 text-[#5288c1]" />
              </div>
              <div>
                <div className="font-semibold text-tg-text">Таймаут</div>
                <div className="text-xs text-tg-hint">Время удержания</div>
              </div>
            </div>
            <div className="text-xl font-bold text-[#5288c1]">
              {timeoutHours % 1 === 0 ? timeoutHours : timeoutHours.toFixed(1)}ч
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <input
              type="range"
              min={0.5}
              max={24}
              step={0.5}
              value={timeoutHours}
              onChange={(e) => {
                setTimeoutHours(parseFloat(e.target.value))
                setError('')
              }}
              onTouchStart={() => hapticFeedback('light')}
              className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb"
              style={{
                background: `linear-gradient(to right, #5288c1 0%, #5288c1 ${((timeoutHours - 0.5) / (24 - 0.5)) * 100}%, rgba(168, 179, 207, 0.2) ${((timeoutHours - 0.5) / (24 - 0.5)) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                ['--slider-color' as any]: '#5288c1'
              }}
            />
            <div className="flex justify-between text-xs text-tg-hint">
              <span>0.5ч</span>
              <span>24ч</span>
            </div>
          </div>

          {/* Preset Buttons */}
          <div className="flex gap-1.5">
            {presetValues.map((hours) => (
              <button
                key={hours}
                onClick={() => {
                  hapticFeedback('light')
                  setTimeoutHours(hours)
                  setError('')
                }}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
                style={
                  timeoutHours === hours
                    ? { backgroundColor: '#5288c1', color: '#ffffff' }
                    : { backgroundColor: '#5288c11a', color: '#5288c1' }
                }
              >
                {hours}ч
              </button>
            ))}
          </div>

          {/* Number Input */}
          <div className="relative">
            <input
              type="number"
              min={0.5}
              max={24}
              step={0.5}
              value={timeoutHours}
              onChange={(e) => {
                const value = parseFloat(e.target.value)
                if (!isNaN(value)) {
                  setTimeoutHours(value)
                  setError('')
                }
              }}
              onFocus={() => hapticFeedback('light')}
              className="w-full px-4 py-2.5 text-sm font-medium text-center border-2 rounded-lg focus:outline-none transition-colors bg-tg-bg text-tg-text"
              style={{
                borderColor: '#5288c133'
              }}
              placeholder="Введите часы"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-tg-hint">
              часов
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-600/10 border-2 border-red-600/20 rounded-xl">
            <p className="text-sm text-red-600 text-center font-medium">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#5288c1] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Подробнее о таймауте</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Таймаут определяет, как долго ресурс остается закрепленным за пользователем
                после его получения. По истечении этого времени пользователь может получить новый ресурс.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
